<template>
  <div class="job-mobile">
    <!-- Filter summary bar -->
    <job-filter-row 
      @click="filterVisible = true"
      :filter="filter"
      :count="rows.length"
      :total="total"
    ></job-filter-row>

    <!-- Action buttons -->
    <div class="container-fluid px-0 my-2">
      <div class="row g-2">
        <div class="col-4">
          <button @click="refresh()"
            class="btn btn-primary form-control"
          >
            <i class="fa fa-sync"></i> Refresh
          </button>
        </div>
        <div class="col-4">
          <button @click="newRecord()"
            class="btn btn-success form-control"
          >
            <i class="fa fa-plus"></i> New
          </button>
        </div>
        <div class="col-4">
          <button @click="filterVisible = true"
            class="btn btn-outline-secondary form-control"
          >
            <i class="fa fa-filter"></i> Filter
          </button>
        </div>
      </div>
    </div>

    <hr class="my-2" />

    <!-- Mobile-optimized list view -->
    <div class="mobile-job-list">
      <div v-for="row in rows" :key="row.Id" class="job-card mb-2" @click="editRecord(row.Id)">
        <div class="card">
          <div class="card-body p-2">
            <div class="d-flex justify-content-between align-items-start mb-2">
              <div class="flex-grow-1">
                <strong>Request #{{ row.Id }}</strong>
                <div class="text-muted small">{{ row.Contact?.Company || 'N/A' }}</div>
              </div>
              <div class="text-end ms-2">
                <span v-if="row.RequestStatus" :class="getStatusBadgeClass(row.RequestStatus)" class="badge">
                  {{ getStatusName(row.RequestStatus) }}
                </span>
              </div>
            </div>
            
            <div class="small mb-1" v-if="row.Subject">
              <strong>{{ row.Subject }}</strong>
            </div>
            
            <div class="small mb-1" v-if="row.PrimaryTechnician?.TechnicianName">
              <i class="fas fa-user me-1"></i>{{ row.PrimaryTechnician.TechnicianName }}
            </div>
            
            <div class="small mb-1" v-if="row.RequestType">
              <i class="fas fa-tag me-1"></i>{{ row.RequestType }}
            </div>
            
            <div class="row small mb-1">
              <div class="col-6" v-if="row.ScheduledStartDate">
                <i class="fas fa-calendar-check me-1"></i>{{ getLocalDate(row.ScheduledStartDate) }}
              </div>
              <div class="col-6 text-end" v-if="row.Priority">
                <span :class="getPriorityBadgeClass(row.Priority)" class="badge badge-sm">
                  Priority: {{ getPriorityLabel(row.Priority) }}
                </span>
              </div>
            </div>
            
            <div class="small text-truncate text-muted" v-if="row.Description">
              {{ row.Description }}
            </div>
          </div>
        </div>
      </div>
      
      <!-- Empty state -->
      <div v-if="rows.length === 0" class="text-center text-muted py-5">
        <i class="fas fa-tasks fa-3x mb-3"></i>
        <p>No requests found</p>
      </div>
    </div>

    <!-- Filter Modal -->
    <kt-modal 
      :visible="filterVisible" 
      title="Filter Requests" 
      @dismiss="filterVisible = false"
    >
      <template #body>
        <job-filter
          :filter="filter" 
          @dismiss="filterVisible = false" 
          @update="setFilter($event)"
        ></job-filter>
      </template>
    </kt-modal>
  </div>
</template>

<style scoped>
.job-mobile {
  padding: 0.5rem;
}

.job-card {
  cursor: pointer;
  transition: transform 0.2s;
}

.job-card:active {
  transform: scale(0.98);
}

.job-card .card {
  border-left: 3px solid #0d6efd;
}

.mobile-job-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}
</style>

<script setup lang="ts">
import { ref } from "vue";
import Helper from "@/services/Helper";
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { usePriorityStore } from "@/stores/PriorityStore";
import { storeToRefs } from "pinia";
import KtModal from "@/components/UI/KtModal.vue";
import JobFilterRow from "@/components/Jobs/JobFilterRow.vue";
import JobFilter from "@/components/Jobs/JobFilter.vue";

// Props - receive data from parent
const props = defineProps<{
  rows: any[];
  total: number;
  filter: any;
}>();

// Emits
const emit = defineEmits<{
  refresh: [];
  newRecord: [];
  editRecord: [id: number];
  setFilter: [filter: any];
}>();

const filterVisible = ref(false);

// Job Status Store
const jobStatusStore = useJobStatusStore();
const { list: JobStatusList } = storeToRefs(jobStatusStore);

// Priority Store
const priorityStore = usePriorityStore();
const { list: PriorityList } = storeToRefs(priorityStore);

// Methods
const refresh = () => {
  emit('refresh');
};

const newRecord = () => {
  emit('newRecord');
};

const editRecord = (id: number) => {
  emit('editRecord', id);
};

const setFilter = (filter: any) => {
  emit('setFilter', filter);
  filterVisible.value = false;
};

const getLocalDate = (val: any) => {
  if (!val) return '';
  let tmp = new Date(val);
  return Helper.dateToInput(tmp, Helper.DateFormat_Date);
};

const getStatusName = (statusId: number) => {
  const status = JobStatusList.value[statusId];
  return status?.Status || `Status #${statusId}`;
};

const getStatusBadgeClass = (statusId: number) => {
  const status = JobStatusList.value[statusId];
  const statusName = status?.Status?.toLowerCase() || '';
  
  if (statusName.includes('completed') || statusName.includes('closed')) {
    return 'bg-success';
  } else if (statusName.includes('pending') || statusName.includes('waiting')) {
    return 'bg-warning';
  } else if (statusName.includes('progress') || statusName.includes('active') || statusName.includes('open')) {
    return 'bg-primary';
  } else if (statusName.includes('cancelled') || statusName.includes('rejected')) {
    return 'bg-danger';
  } else if (statusName.includes('scheduled')) {
    return 'bg-info';
  } else {
    return 'bg-secondary';
  }
};

const getPriorityLabel = (priorityId: number) => {
  const priority = PriorityList.value[priorityId];
  return priority?.Level || `Priority #${priorityId}`;
};

const getPriorityBadgeClass = (priority: number) => {
  // Map priority IDs to badge colors based on your database values
  // 1=Critical, 2=High, 3=Medium, 4=Low, 5=Schedule
  if (priority === 1) return 'bg-danger';      // Critical
  if (priority === 2) return 'bg-warning';     // High
  if (priority === 3) return 'bg-info';        // Medium
  if (priority === 4) return 'bg-secondary';   // Low
  if (priority === 5) return 'bg-primary';     // Schedule
  return 'bg-secondary';
};
</script>
